/**
 * In-memory cache for rendered estimate PDF bytes — the estimate sibling of
 * invoice pdfRenderCache.js.
 *
 * Cache key: `${estimate.id}:${estimate.updated_at}:${status}`. updated_at is the
 * canonical "this estimate changed" signal (status changes + edits bump it).
 *
 * Drafts intentionally bypass the cache: the in-preview editor mutates a draft's
 * contents without necessarily bumping updated_at between renders, so caching a
 * draft would serve stale bytes mid-edit. Every other status is an effectively
 * stable snapshot for preview purposes → cache hits return synchronously.
 *
 * Page-load scoped (no localStorage); LRU eviction via Map insertion order.
 */

const PDF_CACHE = new Map();
const CACHE_MAX = 20;

/** Compute a cache key for an estimate, or null if it shouldn't be cached. */
export function getEstimatePdfCacheKey(estimate, styleTag = '') {
  if (!estimate?.id || !estimate?.updated_at) return null;
  const status = (estimate.status || '').toLowerCase();
  if (!status || status === 'draft') return null;
  // KAN-823: style tag (theme + accent) is part of the key.
  return `${estimate.id}:${estimate.updated_at}:${status}:${styleTag}`;
}

export function readEstimatePdfCache(key) {
  if (!key || !PDF_CACHE.has(key)) return null;
  const bytes = PDF_CACHE.get(key);
  PDF_CACHE.delete(key);
  PDF_CACHE.set(key, bytes);
  return bytes;
}

export function writeEstimatePdfCache(key, bytes) {
  if (!key || !bytes) return;
  if (PDF_CACHE.has(key)) PDF_CACHE.delete(key);
  PDF_CACHE.set(key, bytes);
  while (PDF_CACHE.size > CACHE_MAX) {
    const oldest = PDF_CACHE.keys().next().value;
    PDF_CACHE.delete(oldest);
  }
}

export function clearEstimatePdfCache() {
  PDF_CACHE.clear();
}
